import { BookOpen, ExternalLink, Menu } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, Outlet, ScrollRestoration, useLocation } from "react-router";
import { useAuth } from "../auth/context";
import { consoleNavFor } from "../lib/access";
import { AccountCapacityProvider } from "../features/capacity/AccountCapacityProvider";
import { AccountCapacityWidget } from "./AccountCapacityWidget";
import { ConsoleAccountWidget } from "./ConsoleAccountWidget";
import { DocumentTitle } from "./DocumentTitle";
import { BrandMark } from "./ui/BrandMark";

/** Full-page authenticated shell: sidebar (icon brand, nav, account widget) + content. No top bar. */
export function ConsoleLayout() {
  const { access } = useAuth();
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navItems = consoleNavFor(access);

  return (
    <AccountCapacityProvider>
      <div className="console-shell">
        <DocumentTitle />
        <a className="skip-link" href="#console-main">
          Skip to Content
        </a>

        <aside
          className={sidebarOpen ? "console-sidebar console-sidebar--open" : "console-sidebar"}
        >
          <Link to="/console" className="brand console-sidebar-brand" aria-label="Woven Console">
            <span className="brand-content">
              <BrandMark size={22} />
              <span
                className={pathname === "/console" ? "brand-name brand-name--active" : "brand-name"}
              >
                woven.host
              </span>
            </span>
          </Link>

          <nav className="console-nav" aria-label="Console">
            <ul className="console-nav-list">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/console"}
                    className={({ isActive }) =>
                      isActive ? "console-nav-link console-nav-link--active" : "console-nav-link"
                    }
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon
                      className="console-nav-icon"
                      size={18}
                      strokeWidth={1.6}
                      absoluteStrokeWidth
                      aria-hidden="true"
                    />
                    <span>{item.label}</span>
                    {item.badge ? <span className="nav-badge">{item.badge}</span> : null}
                  </NavLink>
                </li>
              ))}
              <li>
                <a
                  href="/docs"
                  aria-label="Docs (Opens in a New Tab)"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="console-nav-link"
                  onClick={() => setSidebarOpen(false)}
                >
                  <BookOpen
                    className="console-nav-icon"
                    size={18}
                    strokeWidth={1.6}
                    absoluteStrokeWidth
                    aria-hidden="true"
                  />
                  <span>Docs</span>
                  <ExternalLink
                    size={13}
                    strokeWidth={1.6}
                    absoluteStrokeWidth
                    aria-hidden="true"
                  />
                </a>
              </li>
            </ul>
          </nav>

          <div className="console-sidebar-foot">
            <ConsoleAccountWidget />
            <AccountCapacityWidget />
          </div>
        </aside>

        <main id="console-main" className="console-content">
          <button
            type="button"
            className="icon-button console-mobile-toggle"
            aria-label="Toggle Console Menu"
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen((open) => !open)}
          >
            <Menu size={20} strokeWidth={1.7} absoluteStrokeWidth aria-hidden="true" />
          </button>
          <Outlet />
        </main>

        <ScrollRestoration />
      </div>
    </AccountCapacityProvider>
  );
}
